import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Grid, ToggleButton, ToggleButtonGroup, useMediaQuery, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import TitledDrawer from '../../../common/ui/TitledDrawer';
import { PrivateUnavailabilityDrawer } from './PrivateUnavailability/PrivateUnavailabilityDrawer';
import { PrivateSlotAvailabilityDrawer } from './PrivateAvailability/PrivateAvailabilityForm';
import { useCalendar } from '../../CalendarContext/CalendarContext';
import { EditEventDialogContent } from '../EditEventDialog/EditEventDialog';
import { EditCourseDialogContent } from '../EditCourseDialog/EditCourseDialogContent';
import { getOrderPages } from '../../../utils/AccountUtils';
import { AccountData, OrderPageData, OrderPageTypes } from '../../../api';
import { useLanguage } from '../../../common/GeneralUtils';
import { useAuth } from '../../../session/InternalAuthProvider';

enum SelectionType {
  PRIVATE_AVAILABILITY = 'privateAvailability',
  EVENT_AVAILABILITY = 'EventAvailability',
  PRIVATE_UNAVAILABILITY = 'privateUnavailability',
  COURSE_AVAILABILITY = 'CourseAvailability',
}

const getSelectionAndTitle = (
  isUpdatingUnavailability: boolean,
  isUpdatingAvailability: boolean,
  isUpdatingEvent: boolean,
  isUpdatingCourseEvent: boolean,
  hasOnlyEventPages: boolean,
  t: (key: string, options?: Record<string, string>) => string,
  activityLabel?: string,
) => {
  const selection = isUpdatingUnavailability
    ? SelectionType.PRIVATE_UNAVAILABILITY
    : isUpdatingCourseEvent
    ? SelectionType.COURSE_AVAILABILITY
    : isUpdatingEvent
    ? SelectionType.EVENT_AVAILABILITY
    : isUpdatingAvailability
    ? SelectionType.PRIVATE_AVAILABILITY
    : hasOnlyEventPages
    ? SelectionType.EVENT_AVAILABILITY
    : SelectionType.PRIVATE_UNAVAILABILITY;

  const drawerTitle = isUpdatingUnavailability
    ? t('calendar.unavailability_dialog.edit_un_title')
    : isUpdatingCourseEvent
    ? t('calendar.edit_course.edit_course')
    : isUpdatingEvent
    ? t('calendar.edit_event.edit_event')
    : isUpdatingAvailability
    ? t('calendar.unavailability_dialog.edit_title')
    : hasOnlyEventPages
    ? t('calendar.edit_event.create_event')
    : t('calendar.unavailability_dialog.title', { activityLabel: activityLabel || '' });

  return { selection, drawerTitle };
};
export const ExperienceAvailabilityDrawerWrapper = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const {
    closeExperienceAvailabilityDrawer,
    experienceAvailabilityDrawerState: { event, unavailability, privateSlotAvailability, isOpen },
  } = useCalendar();
  const { t } = useTranslation();
  const { authState } = useAuth();
  const language = useLanguage();
  const account = authState.account as unknown as AccountData;
  const orderPages = getOrderPages(account);
  const hasCoursePages =
    orderPages.find((x: OrderPageData) => x.order_page_type === OrderPageTypes.COURSE) !== undefined;
  const hasEventPages = orderPages.find((x: OrderPageData) => x.order_page_type === OrderPageTypes.EVENT) !== undefined;
  const hasPrivatePages =
    orderPages.find((x: OrderPageData) => x.order_page_type === OrderPageTypes.PRIVATE) !== undefined;
  const hasOnlyEventPages = !hasPrivatePages && !hasCoursePages;
  const isUpdatingUnavailability = !!unavailability;
  const isUpdatingAvailability = !!privateSlotAvailability;
  const isUpdatingEvent = !!event;
  const isUpdatingCourseEvent = !!event?.course_id;

  const shouldHideOptions = isUpdatingUnavailability || isUpdatingAvailability || isUpdatingEvent || hasOnlyEventPages;
  const activityLabel = account?.activity_label_plural?.[language];
  const { selection: initialSelection, drawerTitle: initialDraaweTitle } = getSelectionAndTitle(
    isUpdatingUnavailability,
    isUpdatingAvailability,
    isUpdatingEvent,
    isUpdatingCourseEvent,
    hasOnlyEventPages,
    t,
    activityLabel,
  );

  const [selection, setSelection] = useState<SelectionType>(initialSelection);
  const [drawerTitle, setDrawerTitle] = useState(initialDraaweTitle);
  useEffect(() => {
    const { selection: newSelection, drawerTitle: newTitle } = getSelectionAndTitle(
      isUpdatingUnavailability,
      isUpdatingAvailability,
      isUpdatingEvent,
      isUpdatingCourseEvent,
      hasOnlyEventPages,
      t,
      activityLabel,
    );
    setSelection(newSelection);
    setDrawerTitle(newTitle);
  }, [isUpdatingUnavailability, isUpdatingAvailability, isUpdatingEvent, isUpdatingCourseEvent, hasOnlyEventPages]);

  const handleClose = (e: any, reason: any) => {
    if (reason !== 'backdropClick') {
      closeExperienceAvailabilityDrawer();
    }
  };

  const handleSelectionChange = (event: React.MouseEvent<HTMLElement>, newSelection: SelectionType | null) => {
    if (newSelection !== null) {
      setSelection(newSelection);
    }
  };

  return (
    <TitledDrawer onClose={handleClose} open={isOpen} title={drawerTitle}>
      <Grid container flexDirection="column" height={'100%'}>
        {!shouldHideOptions && (
          <Grid
            container
            sx={{ pt: { xs: '12px', md: '18px' }, pb: '12px', paddingInline: { xs: '12px', md: '24px' } }}>
            <ToggleButtonGroup
              fullWidth
              value={selection}
              exclusive
              onChange={handleSelectionChange}
              aria-label="availability selection">
              {hasCoursePages && (
                <ToggleButton value={SelectionType.COURSE_AVAILABILITY} aria-label="course">
                  <Typography variant={isSmallScreen ? undefined : 'h6'}>
                    {t('calendar.edit_course.create_course')}
                  </Typography>
                </ToggleButton>
              )}
              {hasEventPages && (
                <ToggleButton value={SelectionType.EVENT_AVAILABILITY} aria-label="availability">
                  <Typography variant={isSmallScreen ? undefined : 'h6'}>
                    {t('calendar.edit_event.create_event')}
                  </Typography>
                </ToggleButton>
              )}
              {hasPrivatePages && (
                <ToggleButton value={SelectionType.PRIVATE_AVAILABILITY} aria-label="availability">
                  <Typography variant={isSmallScreen ? undefined : 'h6'}>
                    {t('calendar.unavailability_dialog.Buttontitle')}
                  </Typography>
                </ToggleButton>
              )}
              {hasPrivatePages && (
                <ToggleButton value={SelectionType.PRIVATE_UNAVAILABILITY} aria-label="unavailability">
                  <Typography variant={isSmallScreen ? undefined : 'h6'}>
                    {t('calendar.privateSlotAvailabilityDialog.unavliability')}
                  </Typography>
                </ToggleButton>
              )}
            </ToggleButtonGroup>
          </Grid>
        )}
        {!shouldHideOptions && <Divider sx={{ marginInline: { xs: '12px', md: '24px' } }} />}
        <Grid sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
          {selection === SelectionType.PRIVATE_UNAVAILABILITY ? (
            <PrivateUnavailabilityDrawer />
          ) : selection === SelectionType.EVENT_AVAILABILITY ? (
            <EditEventDialogContent />
          ) : selection === SelectionType.COURSE_AVAILABILITY ? (
            <EditCourseDialogContent />
          ) : (
            <PrivateSlotAvailabilityDrawer />
          )}
        </Grid>
      </Grid>
    </TitledDrawer>
  );
};
